import React, { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { number, object } from 'yup';

import CloseIconButton from 'Src/components/buttons/CloseIconButton';
import CTA from 'Src/components/buttons/CTA';
import Copy from 'Src/components/Copy';
import { Flex } from 'Src/components/layout/Flex';
import TitleItems from 'Src/components/TitleItems';
import { useLogger } from 'Src/feature/context-logger/useLogger';
import { getTypedFormComponents } from 'Src/feature/form/getTypedFormComponents';
import { useForm } from 'Src/feature/form/useForm';
import { Modal } from 'Src/feature/modal/Modal';
import { ModalAction } from 'Src/feature/modal/ModalAction';
import { ModalActions } from 'Src/feature/modal/ModalActions';
import { ModalContent } from 'Src/feature/modal/ModalContent';
import { ModalHeader } from 'Src/feature/modal/ModalHeader';
import { ModalTitle } from 'Src/feature/modal/ModalTitle';
import { useModalControl } from 'Src/feature/modal/useModalControl';
import { unknownToNumberOrUndefined } from 'Src/utils/format';

import { useOnErrorShowToast } from '../toasts/useOnErrorShowToast';

import CashBankBillCount, { FormModel as BillCountFormModel } from './CashBankBillCount';
import { useCashBank } from './useCashBank';

import styles from './CashBankEditCloseBank.module.scss';

enum Views {
  MAIN,
  COUNT_BILLS,
  SUMMARY,
}

interface FormModel {
  cashBankId: number;
  amount: number;
  notes?: string;
}

const { Form, Field } = getTypedFormComponents<FormModel>();

const sectionId = 'CashBankEditCloseBank';

interface CashBankEditCloseBankProps {
  onOpenNewBank: () => void;
}

const CashBankEditCloseBank: FC<CashBankEditCloseBankProps> = ({ onOpenNewBank }) => {
  const { t } = useTranslation();
  const log = useLogger().getSubLogger({ name: sectionId });
  const handleFormSubmitError = useOnErrorShowToast({ title: t('cashBank.errors.closeBank') });
  const handlePrintError = useOnErrorShowToast({ title: t('cashBank.errors.printReport') });
  const { data: currentCashBank } = useCashBank.query.current();
  const { mutate: closeOne } = useCashBank.mutate.closeOne(handleFormSubmitError);
  const { mutate: printReport } = useCashBank.mutate.printReport(handlePrintError);

  const form = useForm<FormModel>({
    defaultValues: { cashBankId: currentCashBank?.id },
    schema: object().shape({
      amount: number().label(t('cashBank.labels.depositAmount')).required().min(0),
    })
  });

  const [view, setView] = useState(Views.MAIN);

  const modal = useModalControl({
    onOpen: () => {
      form.action.reset({ cashBankId: currentCashBank?.id });
      setView(Views.MAIN);
    },
  });

  const goToSummary = async () => {
    await form.action.submitIfValid(async () => {
      setView(Views.SUMMARY);
    });
  };

  const onSave = async () => {
    await form.action.submitIfValid(async ({ values }) => {
      // unformatted amount value
      const amount = unknownToNumberOrUndefined(values.amount);
      if (typeof amount !== 'number') log.debug('onSave', 'amount is not a number');
      modal.close();
      closeOne({
        ...values,
        amount: amount as number,
      });
    });
  };

  const onPrint = async () => {
    printReport({ cashBankId: currentCashBank?.id as number });
  };

  const onCountComplete = (billCount: BillCountFormModel) => {
    setView(Views.MAIN);
    const dollarTotal =
      ((billCount.dollar1 || 0) * 1) +
      ((billCount.dollar5 || 0) * 5) +
      ((billCount.dollar10 || 0) * 10) +
      ((billCount.dollar20 || 0) * 20) +
      ((billCount.dollar50 || 0) * 50) +
      ((billCount.dollar100 || 0) * 100);
    const coinTotal =
      ((billCount.coin1 || 0) * 0.01) +
      ((billCount.coin5 || 0) * 0.05) +
      ((billCount.coin10 || 0) * 0.1) +
      ((billCount.coin25 || 0) * 0.25) +
      ((billCount.coin50 || 0) * 0.5) +
      ((billCount.coin100 || 0) * 1);
    const total = dollarTotal + coinTotal;
    form.action.setValue('amount', total);
  };

  const { amount } = form.state.watch();

  const cashBankValues = useMemo(() => {

    const expected = currentCashBank?.expectedAmount as number;
    const actual = amount;
    const realDifference = expected - actual;
    const isNegative = realDifference < 0;
    const difference = Math.abs(realDifference);
    return {
      expected,
      actual,
      difference,
      isNegative,
    };
  }, [currentCashBank?.expectedAmount, amount]);

  return (
    <>
      <CTA onClick={modal.open} label={t('cashBank.labels.closeBank')} />
      <Modal
        open={modal.isOpen}
        onBackDropClick={() => {
          if (view === Views.SUMMARY) void onSave();
          else modal.close();
        }}
      >
        <ModalHeader closeButton={view === Views.SUMMARY ? undefined : <CloseIconButton onClick={modal.close} />}>
          <ModalTitle
            copy={(view === Views.MAIN && t('cashBank.labels.actualAmountInBank')) ||
            (view === Views.COUNT_BILLS && t('cashBank.labels.countTheBills')) ||
            (view === Views.SUMMARY && t('cashBank.labels.bankClosed'))}
          />
        </ModalHeader>

        <ModalContent>
          {(view === Views.MAIN || view === Views.COUNT_BILLS) && (
            <Form usedForm={form} omitChangeTracking>
              <Flex>
                <Flex horizontal>
                  <Field
                    type="currency"
                    name="amount"
                    fullWidth
                    label={t('cashBank.labels.enterAmountDollars')}
                  />
                  <CTA
                    onClick={() => {
                      // treat button as a toggle
                      setView(view === Views.COUNT_BILLS ? Views.MAIN : Views.COUNT_BILLS);
                    }}
                    selected={view === Views.COUNT_BILLS}
                    label={t('cashBank.labels.countBills')}
                  />
                </Flex>
                {view === Views.COUNT_BILLS && <CashBankBillCount onCountComplete={onCountComplete} />}
                <TitleItems
                  variant={['background', 'rounded']}
                  fullWidth
                  items={[
                    { primary: t('cashBank.labels.expectedInTheDrawer'), secondary: <Copy type="currency" value={cashBankValues.expected} /> },
                    { primary: t('cashBank.labels.actualInTheDrawer'), secondary: <Copy type="currency" optional value={cashBankValues.actual} /> },
                    {
                      primary: t('labels.difference'),
                      secondary:
                      cashBankValues.isNegative ?
                        (
                          <div>
                            <Copy color="error" value="(" />
                            <Copy type="currency" color="error" value={cashBankValues.difference} />
                            <Copy color="error" value=")" />
                          </div>
                        ) :
                        (
                          <Copy type="currency" optional value={cashBankValues.difference}/>
                        )
                    },
                  ]} />
                <Field type="text" name="notes" multiline label={t('cashBank.labels.additionalNotes')} />
              </Flex>
            </Form>
          )}

          {view === Views.SUMMARY && (
            <Flex fillItems>
              <Flex className={styles['summary-report']}>
                <Flex horizontal justifyContent="space-between" >
                  <Copy align="right" value={t('cashBank.labels.startingAmount')} />
                  <Copy type="currency" value={currentCashBank?.openAmount} />
                </Flex>

                <Flex horizontal justifyContent="space-between" >
                  <Copy align="right" value={t('cashBank.labels.expectedAmount')} />
                  <Copy type="currency" value={cashBankValues.expected} />
                </Flex>

                <Flex horizontal justifyContent="space-between" >
                  <Copy align="right" value={t('cashBank.labels.endingAmount')} />
                  <Copy type="currency" value={cashBankValues.actual} />
                </Flex>

                <Flex horizontal justifyContent="space-between" >
                  <Copy align="right" value={t('labels.difference')} />
                  <Copy type="currency" value={cashBankValues.difference} />
                </Flex>
              </Flex>
              <CTA label={t('cashBank.labels.reprintReport')} onClick={() => void onPrint()}/>
              <CTA label={t('cashBank.labels.reopenClosedBank')} onClick={modal.close} />
              <CTA
                label={t('cashBank.labels.openNewBank')}
                onClick={() => {
                  void onSave();
                  onOpenNewBank();
                }}
              />
            </Flex>
          )}
        </ModalContent>

        <ModalActions>
          {
            view === Views.MAIN || view === Views.COUNT_BILLS
              ? (
                <ModalAction
                  color="primary"
                  onClick={() => void goToSummary()}
                  label={t('cashBank.labels.goToSummary')}
                />
              )
              : view === Views.SUMMARY && (
                <ModalAction
                  color="primary"
                  onClick={() => void onSave()}
                  label={t('labels.done')}
                />
              )
          }
        </ModalActions>
      </Modal>
    </>
  );
};

export { CashBankEditCloseBank };
